// Loads a device's outputs + last-known status and routes to the correct control
// component — the React equivalent of Flutter's screenRedirector(). Shared by:
//   - DeviceScreen (the /device/:uniqueNumber standalone route)
//   - the desktop shell's right-side device panel (embedded)
//
// Pass a device object (preferred, from a list tile) and/or a uniqueNumber to
// fetch by. `onLoaded(device)` fires once the full record is resolved.

import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { getDeviceDetail, getOutputs, getDeviceStatus } from '../../../../core/api/api';
import { parseStatus } from '../../../../core/mqtt/mqttClient';
import { deviceTypeKey, relayCount, fanCount } from '../../../../core/devices/deviceMeta';
import TouchScreen from './TouchScreen';
import FanScreen from './FanScreen';
import CurtainScreen from './CurtainScreen';
import LightScreen from './LightScreen';
import DoorScreen from './DoorScreen';
import PhaseScreen from './PhaseScreen';
import SensorScreen from './SensorScreen';
import IrRemoteScreen from './IrRemoteScreen';
import UnsupportedScreen from './UnsupportedScreen';
import HubInfo from '../../../shared/HubInfo';
import BleNodePage from './BleNodePage';
import SceneControllerScreen from './SceneControllerScreen';
import { isSensorDevice } from '../../../../core/devices/sensorState';

export default function DeviceControls({ device: initialDevice, uniqueNumber, onLoaded }) {
  const uno = uniqueNumber || (initialDevice && initialDevice.UniqueNumber);
  const [device, setDevice] = useState(initialDevice || null);
  const [outputs, setOutputs] = useState([]);
  const [initialStatus, setInitialStatus] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        let dev = initialDevice;
        if (!dev && uno) {
          const res = await getDeviceDetail(uno);
          dev = Array.isArray(res.data) ? res.data[0] : res.data;
        }
        const [out, st] = await Promise.all([
          getOutputs(uno),
          getDeviceStatus(uno).catch(() => null),
        ]);
        if (alive) {
          setDevice(dev);
          setOutputs(Array.isArray(out.data) ? out.data : []);
          const row = st && Array.isArray(st.data) ? st.data[0] : null;
          setInitialStatus(row && row.Status ? parseStatus(row.Status) : '');
          if (onLoaded && dev) onLoaded(dev);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // Re-run when the target device changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uno]);

  if (loading || !device) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  const props = { device, outputs, initialStatus };

  // ⚠️ BEFORE the switch — its `default` is the outputs-driven fallback, and a
  // sensor's outputs are empty by definition, so that branch could only ever
  // report their absence. See core/devices/sensorState.
  if (isSensorDevice(device)) return <SensorScreen {...props} />;

  const key = deviceTypeKey(device.DeviceID);
  switch (key) {
    case 'oneTouch': return <TouchScreen {...props} gangs={1} />;
    case 'twoTouch': return <TouchScreen {...props} gangs={2} />;
    case 'fourTouch': return <TouchScreen {...props} gangs={4} />;
    /*
     * ⚠️ `switchFan` HAD NO CASE HERE AT ALL, so DeviceIDs 92 and 93 — the 4+1
     * and the 7+1 — fell to UnsupportedScreen on the desktop while the mobile
     * shell drew them properly. Two shells, one device, different answers.
     * `touch` is the same screen for any width the catalogue reports.
     */
    case 'touch':
    case 'switchFan':
      return (
        <TouchScreen
          {...props}
          gangs={relayCount(device.DeviceID)}
          fanChannels={fanCount(device.DeviceID)}
        />
      );
    case 'fan': return <FanScreen {...props} />;
    case 'curtain': return <CurtainScreen {...props} />;
    case 'rgb': return <LightScreen {...props} channels={['R', 'G', 'B']} />;
    case 'wwh': return <LightScreen {...props} channels={['White', 'Warm']} />;
    // No radio on the desktop: say so instead of drawing sliders that publish nowhere.
    // Same panel as the phone; through the location's hub (docs/20).
    case 'bleWwh': return <BleNodePage {...props} />;
    case 'sceneController': return <SceneControllerScreen {...props} />;
    // The hub: a state, no buttons (ui/shared/HubInfo). Also the phone's page —
    // the mobile default branch embeds these controls.
    case 'hub': return <HubInfo device={device} />;
    case 'wwrgb': return <LightScreen {...props} channels={['W', 'WW', 'R', 'G', 'B']} />;
    case 'door': return <DoorScreen {...props} />;
    // Also what the MOBILE default branch embeds, so this one case fixes the
    // "No controllable outputs found." on both shells.
    case 'irBlaster': return <IrRemoteScreen {...props} />;
    case 'singlePhase': return <PhaseScreen {...props} phases={1} />;
    case 'threePhase': return <PhaseScreen {...props} phases={3} />;
    default: return <UnsupportedScreen {...props} />;
  }
}
